import { Link } from "react-router-dom";
import {
  buildPartnerMarketSnapshotStory,
  formatSignedCount,
  priceChangeDirection,
} from "../../../utils/casamap/casaMapaPartnerMarketSnapshot";

const COPY = "realEstate.casaMapa.admin.partnerMarketTrends";

function placeLabel(row) {
  return [row?.neighborhood, row?.city].filter(Boolean).join(", ") || "—";
}

function percentLabel(share) {
  if (share == null) return "—";
  return `${Math.round(share * 100)}%`;
}

function WeeklyStrip({ weekly, t }) {
  const weeks = weekly.slice(-6);
  if (weeks.length < 2) return null;
  const peak = Math.max(
    1,
    ...weeks.flatMap((week) => [Number(week.created) || 0, Number(week.becameUnavailable) || 0])
  );

  return (
    <div className="cm-partner-snapshot__weekly">
      <div className="cm-partner-snapshot__block-head">
        <h3>{t(`${COPY}.weeklyTitle`)}</h3>
        <p>{t(`${COPY}.weeklyHint`)}</p>
      </div>
      <div
        className="cm-partner-snapshot__weeks"
        role="img"
        aria-label={t(`${COPY}.weeklyTitle`)}
        style={{ gridTemplateColumns: `repeat(${weeks.length}, minmax(0, 1fr))` }}
      >
        {weeks.map((week) => {
          const created = Number(week.created) || 0;
          const gone = Number(week.becameUnavailable) || 0;
          return (
            <div key={String(week.weekStart)} className="cm-partner-snapshot__week">
              <div className="cm-partner-snapshot__week-bars">
                <span
                  className="cm-partner-snapshot__week-bar cm-partner-snapshot__week-bar--new"
                  style={{ height: `${Math.max(8, (created / peak) * 100)}%` }}
                  title={t(`${COPY}.weeklyNew`)}
                />
                <span
                  className="cm-partner-snapshot__week-bar cm-partner-snapshot__week-bar--gone"
                  style={{ height: `${Math.max(8, (gone / peak) * 100)}%` }}
                  title={t(`${COPY}.weeklyGone`)}
                />
              </div>
              <span>
                {new Date(week.weekStart).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </span>
            </div>
          );
        })}
      </div>
      <p className="cm-partner-snapshot__week-legend">
        <span className="cm-partner-snapshot__dot cm-partner-snapshot__dot--new" />
        {t(`${COPY}.weeklyNew`)}
        <span className="cm-partner-snapshot__dot cm-partner-snapshot__dot--gone" />
        {t(`${COPY}.weeklyGone`)}
      </p>
    </div>
  );
}

export default function PartnerCasaMapaMarketSnapshot({
  trends,
  error,
  path,
  t,
  formatMoney,
}) {
  const story = trends ? buildPartnerMarketSnapshotStory(trends) : null;
  const cutPct = percentLabel(story?.cutShare);
  const raiseShare = story?.priceMoves ? 1 - story.cutShare : null;

  return (
    <section className="partner-panel cm-partner-snapshot" aria-labelledby="cm-partner-snapshot-title">
      <header className="cm-partner-snapshot__header">
        <div>
          <p className="cm-partner-snapshot__eyebrow">
            {t(`${COPY}.window`, { days: story?.windowDays || 30 })}
          </p>
          <h2 id="cm-partner-snapshot-title">{t(`${COPY}.title`)}</h2>
        </div>
      </header>

      {error ? <p className="admin-data-health-error">{error}</p> : null}
      {!trends && !error ? <p className="muted">{t(`${COPY}.loading`)}</p> : null}

      {story?.hasSignal ? (
        <>
          <p className="cm-partner-snapshot__story">
            {t(`${COPY}.storyLede`, { available: story.available.toLocaleString() })}{" "}
            {t(`${COPY}.storyInventory.${story.inventoryTone}`, {
              created: story.created.toLocaleString(),
              gone: story.gone.toLocaleString(),
            })}{" "}
            {t(`${COPY}.storyPrice.${story.priceTone}`, {
              down: story.decreases.toLocaleString(),
              up: story.increases.toLocaleString(),
            })}
          </p>

          <div className="cm-partner-snapshot__kpis">
            <article>
              <span>{t(`${COPY}.available`)}</span>
              <strong>{story.available.toLocaleString()}</strong>
              <small>{t(`${COPY}.availableHint`)}</small>
            </article>
            {story.inventoryTone === "unclear" ? (
              <article>
                <span>{t(`${COPY}.priceDown`)}</span>
                <strong>{story.decreases.toLocaleString()}</strong>
                <small>{t(`${COPY}.priceDownHint`)}</small>
              </article>
            ) : (
              <article>
                <span>{t(`${COPY}.netFlow`)}</span>
                <strong className={`cm-partner-snapshot__net cm-partner-snapshot__net--${story.inventoryTone}`}>
                  {formatSignedCount(story.netFlow)}
                </strong>
                <small>
                  {story.inventoryTone === "expanding"
                    ? t(`${COPY}.netFlowUp`, { count: story.netFlow.toLocaleString() })
                    : story.inventoryTone === "tightening"
                      ? t(`${COPY}.netFlowDown`, { count: Math.abs(story.netFlow).toLocaleString() })
                      : t(`${COPY}.netFlowFlat`)}
                </small>
              </article>
            )}
            <article>
              <span>{t(`${COPY}.pricePressure`)}</span>
              <strong>{story.priceMoves ? cutPct : "—"}</strong>
              <small>
                {story.priceTone === "cutting"
                  ? t(`${COPY}.pricePressureCuts`, { percent: cutPct })
                  : story.priceTone === "raising"
                    ? t(`${COPY}.pricePressureRaises`, { percent: percentLabel(raiseShare) })
                    : story.priceTone === "balanced"
                      ? t(`${COPY}.pricePressureBalanced`)
                      : t(`${COPY}.pricePressureNone`)}
              </small>
            </article>
            <article>
              <span>{story.inventoryTone === "unclear" ? t(`${COPY}.citiesCount`) : t(`${COPY}.created`)}</span>
              <strong>
                {story.inventoryTone === "unclear"
                  ? story.cities.length.toLocaleString()
                  : story.created.toLocaleString()}
              </strong>
              <small>
                {story.inventoryTone === "unclear" ? t(`${COPY}.citiesCountHint`) : t(`${COPY}.createdHint`)}
              </small>
            </article>
          </div>

          <div className="cm-partner-snapshot__grid">
            <div className="cm-partner-snapshot__pressure">
              <div className="cm-partner-snapshot__block-head">
                <h3>{t(`${COPY}.pressureTitle`)}</h3>
              </div>
              {story.priceMoves ? (
                <>
                  <div className="cm-partner-snapshot__mix" aria-hidden="true">
                    <div
                      className="cm-partner-snapshot__mix-cut"
                      style={{ flexGrow: Math.max(story.decreases, 0.01) }}
                    />
                    <div
                      className="cm-partner-snapshot__mix-up"
                      style={{ flexGrow: Math.max(story.increases, 0.01) }}
                    />
                  </div>
                  <div className="cm-partner-snapshot__mix-labels">
                    <span>
                      {t(`${COPY}.pressureCuts`)} · {story.decreases.toLocaleString()}
                    </span>
                    <span>
                      {t(`${COPY}.pressureRaises`)} · {story.increases.toLocaleString()}
                    </span>
                  </div>
                </>
              ) : (
                <p className="muted">{t(`${COPY}.pricePressureNone`)}</p>
              )}
              <WeeklyStrip weekly={story.weekly} t={t} />
            </div>

            <div className="cm-partner-snapshot__cities">
              <div className="cm-partner-snapshot__block-head">
                <h3>{t(`${COPY}.citiesTitle`)}</h3>
                <p>{t(`${COPY}.citiesHint`)}</p>
              </div>
              {story.cities.length ? (
                <div className="cm-partner-snapshot__table-wrap">
                  <table className="cm-partner-snapshot__table">
                    <thead>
                      <tr>
                        <th>{t(`${COPY}.city`)}</th>
                        <th>{t(`${COPY}.saleCount`)}</th>
                        <th>{t(`${COPY}.saleMedian`)}</th>
                        <th>{t(`${COPY}.rentCount`)}</th>
                        <th>{t(`${COPY}.rentMedian`)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {story.cities.slice(0, 6).map((row) => (
                        <tr key={row.city}>
                          <th scope="row">{row.city}</th>
                          <td>{(row.sale?.properties ?? 0).toLocaleString()}</td>
                          <td>{row.sale?.medianPrice != null ? formatMoney(row.sale.medianPrice) : "—"}</td>
                          <td>{(row.rent?.properties ?? 0).toLocaleString()}</td>
                          <td>{row.rent?.medianPrice != null ? formatMoney(row.rent.medianPrice) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="muted">{t(`${COPY}.storyEmpty`)}</p>
              )}
            </div>
          </div>

          {story.recentPriceChanges.length ? (
            <div className="cm-partner-snapshot__moves">
              <div className="cm-partner-snapshot__block-head">
                <h3>{t(`${COPY}.movesTitle`)}</h3>
                <p>{t(`${COPY}.movesHint`)}</p>
              </div>
              <ul>
                {story.recentPriceChanges.slice(0, 5).map((row, index) => {
                  const direction = priceChangeDirection(row);
                  const percent = Number(row.changePercent);
                  const percentText = Number.isFinite(percent)
                    ? `${percent > 0 ? "+" : ""}${percent.toFixed(1)}%`
                    : null;
                  return (
                    <li key={`${row.title || row.city}-${row.changedAt || index}`}>
                      <div>
                        <strong>{row.title || placeLabel(row)}</strong>
                        <span>{placeLabel(row)}</span>
                      </div>
                      <div className={`cm-partner-snapshot__delta cm-partner-snapshot__delta--${direction}`}>
                        <span>
                          {row.previousPrice != null ? formatMoney(row.previousPrice) : "—"}
                          {" → "}
                          {row.newPrice != null ? formatMoney(row.newPrice) : "—"}
                        </span>
                        {percentText ? <em>{percentText}</em> : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          <div className="cm-partner-snapshot__actions">
            <Link className="partner-dashboard-btn partner-dashboard-btn--primary" to={path("market/heatmap")}>
              {t(`${COPY}.openHeatmap`)}
            </Link>
            <Link className="partner-dashboard-btn" to={path("market/brief")}>
              {t(`${COPY}.openBrief`)}
            </Link>
            <Link className="partner-dashboard-btn" to={path("market/pulse")}>
              {t(`${COPY}.openPulse`)}
            </Link>
          </div>
        </>
      ) : trends ? (
        <p className="muted">{t(`${COPY}.storyEmpty`)}</p>
      ) : null}
    </section>
  );
}
